import { NpiActivity, NpiActivityGroup, NpiActivityStatus, NpiProject, NpiResource } from "../types/npi-project.types";
import { CalendarType, calendarTypeLabels } from "../../../../../models/basic/template";
import { translate } from "../../../../../translate/translate";

const RESOURCE_LABEL_TO_NPI_RESOURCE: Record<string, NpiResource> = {
    [calendarTypeLabels[CalendarType.CHINA]]: "China",
    [calendarTypeLabels[CalendarType.MANAUS]]: "Manaus",
    [calendarTypeLabels[CalendarType.SAO_PAULO]]: "Sao Paulo",
};

const MONTH_ABBREVIATIONS: Record<string, string[]> = {
    pt: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
    en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

export const shortDate = (iso: string | null): string => {
    if (!iso) {
        return "";
    }
    const [year, month, day] = iso.split("-");
    const months = MONTH_ABBREVIATIONS[translate.language === "pt" ? "pt" : "en"];
    return `${day}-${months[Number(month) - 1]}-${year.slice(2)}`;
};

const normalizeGroupId = (value: any): number | null => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    if (typeof value === "object") {
        return normalizeGroupId(value.id);
    }
    const id = Number(value);
    return Number.isNaN(id) ? null : id;
};

const mapTask = (raw: any): NpiActivity => ({
    id: raw.id,
    number: "",
    name: raw.name,
    acronym: raw.acronym ?? "",
    showInDashboard: raw.show_in_dashboard ?? true,
    resource: RESOURCE_LABEL_TO_NPI_RESOURCE[calendarTypeLabels[raw.resource as CalendarType]] ?? "Sao Paulo",
    duration: raw.duration,
    baselineStart: shortDate(raw.baseline_start),
    baselineEnd: shortDate(raw.baseline_end),
    baselineEndDate: raw.baseline_end ?? null,
    executionStart: raw.execution_start ?? null,
    executionEnd: raw.execution_end ?? null,
    status: raw.status as NpiActivityStatus,
    delayDays: raw.delay_days ?? 0,
    dependencies: raw.predecessor_detail ?? [],
    statusFinal: raw.status_final,
});

type MappedGroup = NpiActivityGroup & {
    baselineStarts: string[];
    baselineEnds: string[];
};

const getNestedGroups = (raw: any): any[] => raw.subgroups ?? raw.children ?? raw.groups ?? [];

const mapGroup = (raw: any, ancestorGroup?: number | null): MappedGroup => {
    const tasks = raw.tasks ?? [];
    const starts = tasks.map((t: any) => t.baseline_start).filter(Boolean).sort();
    const ends = tasks.map((t: any) => t.baseline_end).filter(Boolean).sort();
    const parentId = ancestorGroup !== undefined
        ? ancestorGroup
        : normalizeGroupId(raw.ancestor_group ?? raw.parent_group ?? raw.parent ?? raw.parent_id);

    return {
        id: raw.id,
        number: "",
        name: raw.name,
        ancestorGroup: parentId,
        order: raw.order ?? 0,
        startDate: shortDate(starts[0] ?? null),
        endDate: shortDate(ends[ends.length - 1] ?? null),
        progress: raw.progress ?? 0,
        tasks: tasks.map(mapTask),
        children: getNestedGroups(raw).map((child) => mapGroup(child, raw.id)),
        baselineStarts: starts,
        baselineEnds: ends,
    };
};

const collectDates = (group: MappedGroup, field: "baselineStarts" | "baselineEnds"): string[] => [
    ...group[field],
    ...group.children.flatMap((child) => collectDates(child as MappedGroup, field)),
];

const collectActivities = (group: MappedGroup): NpiActivity[] => [
    ...group.tasks,
    ...group.children.flatMap((child) => collectActivities(child as MappedGroup)),
];

const normalizeGroupDatesAndProgress = (group: MappedGroup): MappedGroup => {
    group.children = group.children.map((child) => normalizeGroupDatesAndProgress(child as MappedGroup));

    const starts = collectDates(group, "baselineStarts").sort();
    const ends = collectDates(group, "baselineEnds").sort();
    const activities = collectActivities(group).filter((activity) => activity.status !== "nao_sera_executada");
    const completedActivities = activities.filter((activity) => activity.status === "concluido").length;

    group.startDate = shortDate(starts[0] ?? null);
    group.endDate = shortDate(ends[ends.length - 1] ?? null);
    group.progress = group.progress || (activities.length ? Math.round((completedActivities / activities.length) * 100) : 0);

    return group;
};

const cleanGroup = (group: MappedGroup): NpiActivityGroup => {
    const { baselineStarts, baselineEnds, ...cleanedGroup } = group;
    return {
        ...cleanedGroup,
        children: group.children.map((child) => cleanGroup(child as MappedGroup)),
    };
};

const assignSequenceNumbers = (groups: NpiActivityGroup[]): NpiActivityGroup[] => {
    let sequence = 0;

    const numberGroup = (group: NpiActivityGroup): NpiActivityGroup => {
        const number = String(sequence++);
        const tasks = group.tasks.map((activity) => ({ ...activity, number: String(sequence++) }));
        return { ...group, number, tasks, children: group.children.map(numberGroup) };
    };

    const numberedGroups = groups.map(numberGroup);

    const numbersById = new Map<number, string>();
    const collectNumbers = (group: NpiActivityGroup) => {
        group.tasks.forEach((activity) => numbersById.set(activity.id, activity.number));
        group.children.forEach(collectNumbers);
    };
    numberedGroups.forEach(collectNumbers);

    const numberDependencies = (group: NpiActivityGroup): NpiActivityGroup => ({
        ...group,
        tasks: group.tasks.map((activity) => ({
            ...activity,
            dependencies: activity.dependencies?.map((dep) => ({ ...dep, number: numbersById.get(dep.id) })),
        })),
        children: group.children.map(numberDependencies),
    });

    return numberedGroups.map(numberDependencies);
};

const mapGroups = (rawGroups: any[]): NpiActivityGroup[] => {
    const mappedGroups = rawGroups.map((group) => mapGroup(group));
    const groupsById = new Map<number, MappedGroup>();

    mappedGroups.forEach((group) => {
        groupsById.set(group.id, {
            ...group,
            children: [...group.children],
        });
    });

    groupsById.forEach((group) => {
        if (group.ancestorGroup === null || group.ancestorGroup === undefined) {
            return;
        }
        const parent = groupsById.get(group.ancestorGroup);
        if (!parent) {
            return;
        }
        parent.children = parent.children.filter((child) => child.id !== group.id);
        parent.children.push(group);
    });

    return Array.from(groupsById.values())
        .filter((group) => group.ancestorGroup === null || group.ancestorGroup === undefined || !groupsById.has(group.ancestorGroup))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((group) => cleanGroup(normalizeGroupDatesAndProgress(group)));
};

export const mapNpiDetailToProject = (raw: any): NpiProject => ({
    id: raw.id,
    client: raw.project?.client ?? "",
    projectName: raw.project?.name ?? "",
    progress: raw.progress ?? 0,
    completedActivities: raw.completed_activities ?? 0,
    totalActivities: raw.total_activities ?? 0,
    frc: shortDate(raw.end_date),
    groups: assignSequenceNumbers(mapGroups(raw.groups ?? [])),
});
